import { useEffect, useState, useCallback } from "react";
import { Bell, Check, Trash } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function NotificationBell({ pollUrl }) {
  const [items, setItems] = useState([]);
  const unread = items.filter((n) => !n.gelesen).length;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, gelesen: true } : n)));
    try { await api.post(`/notifications/${id}/read`); } catch { /* ignore */ }
  };

  const remove = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch { /* ignore */ }
  };

  const removeAll = async () => {
    setItems([]);
    try { await api.delete("/notifications"); } catch { /* ignore */ }
  };

  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative flex h-10 w-10 items-center justify-center rounded-full text-[#0A1128] transition-colors hover:bg-black/5"
          aria-label="Benachrichtigungen"
        >
          <Bell size={22} weight="regular" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#C5A059] px-1 text-[10px] font-bold text-white"
            >
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] max-w-[92vw] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-[#E2E8F0] px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Benachrichtigungen</span>
          <div className="flex items-center gap-3">
            {unread > 0 && (
              <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-[#718096] transition-colors hover:text-[#0A1128]">
                Alle gelesen
              </button>
            )}
            {items.length > 0 && (
              <button onClick={removeAll} data-testid="delete-all-notifications" className="flex items-center gap-1 text-xs text-[#C0392B] transition-colors hover:text-[#96281B]">
                <Trash size={13} weight="bold" /> Alle löschen
              </button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-[#A0AEC0]">Keine Benachrichtigungen</p>
          )}
          {items.map((n) => (
            <div
              key={n.id}
              data-testid="notification-item"
              className={`flex items-start gap-2 border-b border-[#F1F5F9] px-3 py-3.5 text-sm ${n.gelesen ? "text-[#718096]" : "bg-[#FBF8F1] text-[#0A1128]"}`}
            >
              <div className="min-w-0 flex-1 leading-relaxed">
                {n.text}
                {n.erstellt_am && (
                  <span className="mt-1 block text-[10px] text-[#A0AEC0]">
                    {new Date(n.erstellt_am).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {!n.gelesen && (
                  <button
                    onClick={() => markRead(n.id)}
                    data-testid="notification-mark-read"
                    aria-label="Als gelesen markieren"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#718096] transition-colors hover:bg-[#C5A059]/15 hover:text-[#8a6d2f]"
                  >
                    <Check size={16} weight="bold" />
                  </button>
                )}
                <button
                  onClick={() => remove(n.id)}
                  data-testid="notification-delete"
                  aria-label="Löschen"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-[#C0392B] transition-colors hover:bg-[#C0392B]/10"
                >
                  <Trash size={16} weight="regular" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
